import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dates: '',
    guests: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6iq2codenotc3br482g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', dates: '', guests: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="w-full py-16 md:py-24 lg:py-32 px-6 md:px-12 lg:px-24 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Column - Contact Info */}
          <div>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-thin tracking-wider text-gray-900 mb-8 lg:mb-12"
              style={{ letterSpacing: '2px' }}
            >
              GET IN TOUCH
            </h2>
            <p className="text-lg md:text-xl font-light text-gray-600 mb-10 lg:mb-16 leading-relaxed">
              Ready to experience the pinnacle of alpine luxury? Our team is here to help you plan your perfect mountain escape.
            </p>

            <div className="space-y-6 md:space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#F0EDE8' }}>
                  <i className="ri-map-pin-line text-xl" style={{ color: '#2B2B2B' }}></i>
                </div>
                <div>
                  <p className="text-sm font-light text-gray-500 mb-1">Location</p>
                  <p className="text-lg font-light text-gray-900">Châtel, French Alps, France</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#F0EDE8' }}>
                  <i className="ri-mail-line text-xl" style={{ color: '#2B2B2B' }}></i>
                </div>
                <div>
                  <p className="text-sm font-light text-gray-500 mb-1">Email</p>
                  <p className="text-base md:text-lg font-light text-gray-900 break-all">info@vonnesalpinelife.eu</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#F0EDE8' }}>
                  <i className="ri-phone-line text-xl" style={{ color: '#2B2B2B' }}></i>
                </div>
                <div>
                  <p className="text-sm font-light text-gray-500 mb-1">Phone</p>
                  <p className="text-lg font-light text-gray-900">+33 4 50 73 22 44</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div>
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:border-gray-900 focus:outline-none transition-colors text-base"
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:border-gray-900 focus:outline-none transition-colors text-base"
                />
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:border-gray-900 focus:outline-none transition-colors text-base"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <input
                  type="text"
                  name="dates"
                  placeholder="Preferred Dates"
                  value={formData.dates}
                  onChange={(e) => setFormData({ ...formData, dates: e.target.value })}
                  className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:border-gray-900 focus:outline-none transition-colors text-base"
                />
                <input
                  type="text"
                  name="guests"
                  placeholder="Number of Guests"
                  value={formData.guests}
                  onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                  className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:border-gray-900 focus:outline-none transition-colors text-base"
                />
              </div>

              <div>
                <textarea
                  name="message"
                  placeholder="Tell us about your ideal alpine experience..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  maxLength={500}
                  rows={5}
                  className="w-full px-6 py-4 rounded-lg border border-gray-300 focus:border-gray-900 focus:outline-none transition-colors resize-none text-base"
                ></textarea>
                <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-5 rounded-lg text-white text-lg font-light tracking-wide transition-all duration-300 hover:opacity-90 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: '#2B2B2B' }}
              >
                {isSubmitting ? 'Sending...' : 'Send Inquiry'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#D4AF37', color: '#2B2B2B' }}>
                  <p className="text-center font-light">Thank you! We'll be in touch shortly.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 rounded-lg bg-red-100 text-red-800">
                  <p className="text-center font-light">Something went wrong. Please try again.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}